/* ========================================
   UNIVERSAL RECAPTCHA PROTECTION
   This file adds security to all your uploads and forms
   Version: 1.0 - Beginner Friendly
   ======================================== */

/* ============================================
   PRODUCTION MODE CONFIGURATION
   ============================================ */
const RECAPTCHA_PRODUCTION_MODE = true; // Set to false when testing/debugging

const logger = {
  dev: (...args) => {
    if (!RECAPTCHA_PRODUCTION_MODE) {
      console.log(...args);
    }
  },
  error: (...args) => console.error(...args),
  warn: (...args) => console.warn(...args),
  info: (message) => {
    if (!RECAPTCHA_PRODUCTION_MODE) {
      console.log(message);
    } else {
      // Only show user-friendly messages in production
      if (message.includes('✅') || message.includes('🎉') || message.includes('✓')) {
        console.log(message);
      }
    }
  }
};


logger.dev('🛡️ Universal reCAPTCHA Protection Loading...');

/* ========================================
   CONFIGURATION
   ======================================== */

// Your reCAPTCHA settings (already set up!)
window.UNIVERSAL_RECAPTCHA_CONFIG = {
  siteKey: '6LeMbJkrAAAAAE9MDSRFl796-ECr7-U0qcvWnxIl',
  enabled: true,
  actions: {
    FILE_UPLOAD: 'file_upload',
    FORM_SUBMIT: 'form_submit',
    TEXT_MODULE: 'text_module'
  }
};

/* ========================================
   HELPER FUNCTIONS (You don't need to understand these)
   ======================================== */

/**
 * GET RECAPTCHA TOKEN
 * This gets a security token from Google reCAPTCHA
 */
function getUniversalRecaptchaToken(action) {
  return new Promise((resolve, reject) => {
    
    // Check if reCAPTCHA is ready
    function checkRecaptcha() {
      if (typeof grecaptcha !== 'undefined' && grecaptcha.ready) {
        grecaptcha.ready(function() {
          grecaptcha.execute(window.UNIVERSAL_RECAPTCHA_CONFIG.siteKey, {action: action})
            .then(function(token) {
              logger.dev('✅ reCAPTCHA token generated for:', action);
              resolve(token);
            })
            .catch(function(error) {
              console.error('reCAPTCHA error:', error);
              reject(error);
            });
        });
      } else {
        // reCAPTCHA not ready yet, wait and check again
        setTimeout(checkRecaptcha, 100);
      }
    }
    
    checkRecaptcha();
    
    // Give up after 10 seconds
    setTimeout(() => {
      reject(new Error('reCAPTCHA timeout'));
    }, 10000);
  });
}

/**
 * SHOW "VERIFYING..." MESSAGE
 */
function showVerifyingMessage(button) {
  if (button) {
    button.disabled = true;
    button.setAttribute('data-original-text', button.innerHTML);
    button.innerHTML = '🔒 Verifying...';
    button.classList.add('recaptcha-loading');
  }
}

/**
 * HIDE "VERIFYING..." MESSAGE
 */
function hideVerifyingMessage(button) {
  if (button) {
    button.disabled = false;
    const originalText = button.getAttribute('data-original-text');
    if (originalText) {
      button.innerHTML = originalText;
    }
    button.classList.remove('recaptcha-loading');
  }
}

/* ========================================
   PROTECT FILE UPLOADS AUTOMATICALLY
   ======================================== */

document.addEventListener('DOMContentLoaded', function() {
  logger.dev('🔍 Looking for file uploads to protect...');
  
  // Find the file upload input
  const fileInput = document.getElementById('product-image');
  const submitBtn = document.querySelector('.submit-btn');
  
  if (fileInput) {
    logger.dev('✅ Found file upload - adding reCAPTCHA protection');
    
    // Store the selected file
    let protectedFile = null;
    
    fileInput.addEventListener('change', async function(e) {
      const file = e.target.files[0];
      
      if (!file) {
        protectedFile = null;
        return;
      }
      
      logger.dev('🔒 File selected - getting reCAPTCHA token...');
      
      try {
        // Show user we're checking security
        showVerifyingMessage(submitBtn);
        
        // Get security token
        const token = await getUniversalRecaptchaToken(
          window.UNIVERSAL_RECAPTCHA_CONFIG.actions.FILE_UPLOAD
        );
        
        // Save the file with its security token
        protectedFile = file;
        protectedFile.recaptchaToken = token;
        
        // Store it globally so form can use it
        window.selectedFileWithRecaptcha = protectedFile;
        
        logger.dev('✅ File protected with reCAPTCHA');
        
      } catch (error) {
        console.error('❌ reCAPTCHA verification failed:', error);
        alert('Security verification failed. Please try again.');
        fileInput.value = '';
        protectedFile = null;
      } finally {
        hideVerifyingMessage(submitBtn);
      }
    });
  }
});

/* ========================================
   PROTECT FORM SUBMISSIONS AUTOMATICALLY
   ======================================== */

document.addEventListener('DOMContentLoaded', function() {
  logger.dev('🔍 Looking for forms to protect...');
  
  const form = document.querySelector('.custom-engraving-form');
  
  if (form) {
    logger.dev('✅ Found form - adding reCAPTCHA protection');
    
    form.addEventListener('submit', async function(e) {
      e.preventDefault(); // Stop normal submission
      
      const submitBtn = form.querySelector('.submit-btn');
      const originalText = submitBtn ? submitBtn.innerHTML : '';
      
      try {
        logger.dev('🔒 Form submitting - getting reCAPTCHA token...');
        
        // Show user we're submitting securely
        showVerifyingMessage(submitBtn);
        
        // Get security token for form
        const formToken = await getUniversalRecaptchaToken(
          window.UNIVERSAL_RECAPTCHA_CONFIG.actions.FORM_SUBMIT
        );
        
        // Gather form data
        const formData = {
          name: document.getElementById('customer-name')?.value.trim(),
          email: document.getElementById('customer-email')?.value.trim(),
          phone: document.getElementById('customer-phone')?.value.trim() || '',
          engravingIdea: document.getElementById('engraving-idea')?.value.trim(),
          recaptchaToken: formToken
        };
        
        // Add file if one was uploaded
        if (window.selectedFileWithRecaptcha) {
          const fileBase64 = await convertFileToBase64(window.selectedFileWithRecaptcha);
          formData.fileData = fileBase64.split(',')[1];
          formData.fileName = window.selectedFileWithRecaptcha.name;
          formData.fileType = window.selectedFileWithRecaptcha.type;
          formData.recaptchaToken = window.selectedFileWithRecaptcha.recaptchaToken || formToken;
        }
        
        logger.dev('📤 Sending protected form data...');
        
        // Send to your server
        const response = await fetch(window.APPS_SCRIPT_URL || 'https://engraverslabdrive.nick-aff.workers.dev/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        });
        
        const result = await response.json();
        
        if (result.success) {
          logger.dev('✅ Form submitted successfully with reCAPTCHA protection');
          alert('✅ ' + result.message);
          form.reset();
          window.selectedFileWithRecaptcha = null;
        } else {
          alert('❌ ' + (result.message || 'Submission failed'));
        }
        
      } catch (error) {
        console.error('❌ Form submission error:', error);
        alert('Network error. Please check your connection and try again.');
      } finally {
        hideVerifyingMessage(submitBtn);
      }
    });
  }
});

/* ========================================
   PROTECT TEXT MODULES AUTOMATICALLY
   ======================================== */

document.addEventListener('DOMContentLoaded', function() {
  logger.dev('🔍 Checking for text modules to protect...');
  
  // Wait a moment for text modules to load
  setTimeout(function() {
    
    // Check if PetTextModule exists
    if (typeof PetTextModule !== 'undefined') {
      logger.dev('✅ Found text modules - adding reCAPTCHA protection');
      
      // Save the original save function
      const originalSave = PetTextModule.prototype.saveCustomTextData;
      
      // Replace it with a protected version
      PetTextModule.prototype.saveCustomTextData = async function() {
        try {
          logger.dev('🔒 Text module saving - getting reCAPTCHA token...');
          
          // Get security token
          const token = await getUniversalRecaptchaToken(
            window.UNIVERSAL_RECAPTCHA_CONFIG.actions.TEXT_MODULE
          );
          
          // Get the original data
          const customTextData = this.packageCustomTextData();
          
          // Create unique ID
          const timestamp = Date.now();
          const randomPart = Math.random().toString(36).substr(2, 9);
          const customTextId = `${this.settings.moduleTypeId.replace('-', '_')}_${timestamp}_${randomPart}`;
          
          // Prepare data with reCAPTCHA token
          const uploadData = {
            action: 'savePetTagData',
            petTagId: customTextId,
            petTagData: customTextData,
            maxCharsPerLine: this.settings.maxCharsPerLine,
            maxLinesFront: this.settings.maxLinesFront,
            maxLinesBack: this.settings.maxLinesBack,
            moduleType: this.settings.moduleTypeId,
            productId: this.container.dataset.productId,
            productTitle: this.productData.title || 'Custom Text Item',
            variantId: this.selectedVariant.id,
            variantTitle: this.selectedVariant.title,
            source: `${this.settings.moduleTypeId}_module`,
            timestamp: new Date().toISOString(),
            recaptchaToken: token // The important security token!
          };
          
          // Send to server
          const response = await fetch('https://engraverslabdrive.nick-aff.workers.dev/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(uploadData)
          });
          
          if (!response.ok) {
            throw new Error(`Save failed: ${response.status}`);
          }
          
          const result = await response.json();
          
          if (!result.success) {
            throw new Error(result.message || 'Save failed');
          }
          
          logger.dev(`✅ Text module saved with reCAPTCHA: ${result.petTagId}`);
          
          return {
            customTextId: result.petTagId,
            fileId: result.fileId,
            fileName: result.fileName,
            fileUrl: result.fileUrl,
            message: result.message
          };
          
        } catch (error) {
          console.error('❌ Text module save failed:', error);
          throw error;
        }
      };
      
      logger.dev('✅ Text modules are now protected with reCAPTCHA');
    }
    
  }, 1000); // Wait 1 second for modules to load
});

/* ========================================
   HELPER UTILITIES
   ======================================== */

/**
 * Convert file to base64 format
 */
function convertFileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/* ========================================
   INITIALIZATION COMPLETE
   ======================================== */

logger.info('✅ Store security active');
logger.dev('🛡️ Protecting:');
logger.dev('   • File uploads');
logger.dev('   • Form submissions');
logger.dev('   • Text modules (pet tags, nameplates, etc.)');